import { cn } from '@/lib/cn';
import { useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { ResolvedUIConfigWithTest } from '../jsonForms/DisplayJsonUi';
import { DisplayJsonUiField } from '../jsonForms/DisplayJsonUiField';
import { ConfigurationState } from 'ui/editor/dataStream/TileEditor/state/TileEditorState';

interface FieldGroupTabsProps {
    fields: ResolvedUIConfigWithTest[];
    data: Record<string, string>;
    autoFocus: boolean;
    disabled: boolean;
    defaultTab?: number;
    menuPortalTarget?: HTMLElement | null;
    configurationState?: ConfigurationState[string];
    handleConfigurationStateChange?: (data: Record<string, string | number>) => void;
}

export const FieldGroupTabs: React.FC<FieldGroupTabsProps> = ({ 
    fields, 
    autoFocus,
    defaultTab, 
    configurationState, 
    handleConfigurationStateChange,
    ...fieldProps 
}) => {
    const [activeTab, setActiveTab] = useState(configurationState?.defaultTab as number ?? defaultTab ?? 0);
    const tabs = fields.filter(field => field.type === 'fieldGroup' && field.displayAs === 'tab');

    return (
        <Tabs 
            selectedIndex={activeTab}
            forceRenderTabPanel={true}
            onSelect={(selectedIndex) => {
                setActiveTab(selectedIndex);
                handleConfigurationStateChange?.({ defaultTab: selectedIndex });
            }}
            className='flex flex-col min-h-0'
        >
            <TabList className='relative flex w-full pb-px overflow-x-scroll shrink-0 text-textSecondary scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary'>
                <div className='absolute bottom-0 left-0 w-full h-px bg-dividerPrimary'/>

                {tabs.map((tab: ResolvedUIConfigWithTest, tabIndex) =>
                    <Tab 
                        key={tab.name}
                        selectedClassName='text-textPrimary'
                        className={cn(
                            'shrink-0 py-4 px-6 text-center select-none relative text-sm font-semibold cursor-pointer focus:ring-0 outline-none aria-[disabled=true]:opacity-50 aria-[disabled=true]:cursor-not-allowed',
                            tabs.length === 1 && 'cursor-auto'
                        )}
                    >
                        <span>{'label' in tab ? tab.label : tab.name}</span>

                        {activeTab === tabIndex && tabs.length > 1 && (
                            <div
                                className='absolute -bottom-0 left-0 w-full bg-primaryButtonBackground h-[2px]'
                                data-selected-tab={tab.name}
                            />
                        )}
                    </Tab>
                )}
            </TabList>

            {tabs.map((tab, tabIndex) =>
                <TabPanel 
                    key={tab.name}
                    className={cn('invisible w-0 h-0 overflow-hidden flex flex-col', tabIndex === activeTab && 'visible w-auto overflow-auto tile-scroll-overflow h-auto pl-6 pr-5 pt-4')}
                >
                    {'fields' in tab && tab.fields.map((field, index) =>
                        <DisplayJsonUiField
                            key={field.name}
                            index={index}
                            field={field as ResolvedUIConfigWithTest}
                            autoFocus={tabIndex === 0 && index === 0 && autoFocus}
                            {...fieldProps}
                        />
                    )}
                </TabPanel>
            )}
        </Tabs>
    );
};